import { useCallback, type ReactElement } from 'react';
import {
  AlignJustify,
  AlignCenter,
  AlignLeft,
  Minus,
  Plus,
  RotateCcw,
} from 'lucide-react';
import {
  READING_FONT_SCALE_DEFAULT,
  READING_FONT_SCALE_MAX,
  READING_FONT_SCALE_MIN,
  READING_FONT_SCALE_STEP,
  READING_WIDTHS,
  useDocStore,
  type ReadingWidth,
} from '../store/docStore';

const WIDTH_OPTIONS: { value: ReadingWidth; label: string; Icon: typeof AlignCenter }[] = [
  { value: 'narrow', label: 'Narrow column', Icon: AlignCenter },
  { value: 'wide', label: 'Wide column', Icon: AlignJustify },
  { value: 'full', label: 'Full width', Icon: AlignLeft },
];

/**
 * Floating zoom + width control for the reading view. Anchored top-right of
 * the reading pane (positioned by `theme/index.css`). All state is read from
 * and written to `useDocStore`, which persists both values to localStorage so
 * the user's preference survives reloads.
 *
 * Keyboard: the underlying app already binds Cmd/Ctrl-+ / − / 0 to the same
 * zoom actions (see `App.tsx`); the buttons here are a visible affordance and
 * an entry point for users who don't know the shortcut.
 */
export function ReadingControls(): ReactElement {
  const scale = useDocStore((s) => s.readingFontScale);
  const width = useDocStore((s) => s.readingWidth);
  const nudgeScale = useDocStore((s) => s.nudgeReadingFontScale);
  const resetScale = useDocStore((s) => s.resetReadingFontScale);
  const setWidth = useDocStore((s) => s.setReadingWidth);

  const zoomOutDisabled = scale <= READING_FONT_SCALE_MIN + 1e-6;
  const zoomInDisabled = scale >= READING_FONT_SCALE_MAX - 1e-6;
  const resetDisabled = Math.abs(scale - READING_FONT_SCALE_DEFAULT) < 1e-6;
  const percent = Math.round(scale * 100);

  const onZoomOut = useCallback(
    () => nudgeScale(-READING_FONT_SCALE_STEP),
    [nudgeScale],
  );
  const onZoomIn = useCallback(
    () => nudgeScale(READING_FONT_SCALE_STEP),
    [nudgeScale],
  );

  return (
    <div
      className="mp-reading-controls"
      role="toolbar"
      aria-label="Reading view controls"
    >
      <div
        className="mp-reading-controls__group mp-reading-controls__group--zoom"
        role="group"
        aria-label="Text zoom"
      >
        <button
          type="button"
          className="mp-reading-controls__btn"
          onClick={onZoomOut}
          disabled={zoomOutDisabled}
          aria-label="Decrease text size"
          title="Decrease text size"
        >
          <Minus size={14} aria-hidden strokeWidth={2.25} />
        </button>
        <button
          type="button"
          className="mp-reading-controls__percent"
          onClick={resetScale}
          disabled={resetDisabled}
          aria-label={`Reset text zoom to 100% (current ${percent}%)`}
          title="Click to reset to 100%"
        >
          {percent}%
        </button>
        <button
          type="button"
          className="mp-reading-controls__btn"
          onClick={onZoomIn}
          disabled={zoomInDisabled}
          aria-label="Increase text size"
          title="Increase text size"
        >
          <Plus size={14} aria-hidden strokeWidth={2.25} />
        </button>
        {!resetDisabled && (
          <button
            type="button"
            className="mp-reading-controls__btn mp-reading-controls__btn--reset"
            onClick={resetScale}
            aria-label="Reset text zoom"
            title="Reset to 100%"
          >
            <RotateCcw size={13} aria-hidden strokeWidth={2.25} />
          </button>
        )}
      </div>
      <div
        className="mp-reading-controls__group mp-reading-controls__group--width"
        role="group"
        aria-label="Content width"
      >
        {WIDTH_OPTIONS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            className="mp-reading-controls__btn mp-reading-controls__width"
            aria-pressed={width === value}
            aria-label={label}
            title={label}
            onClick={() => setWidth(value)}
            disabled={!READING_WIDTHS.includes(value)}
          >
            <Icon size={14} aria-hidden strokeWidth={2} />
          </button>
        ))}
      </div>
    </div>
  );
}
